import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Card, CircularProgress, Sheet, Stack, Table, Typography } from '@mui/joy';
import type { DirectoryAccount } from '../modules/accounts/accounts.model';
import { cisService } from '../services/cisService';
import type { CISMonthlyPeriod, CISSummary } from '../types/cis.types';
import { downloadCisCertificate } from './CISView';

const money = (n: number) => `£${(Number.isFinite(n) ? n : 0).toFixed(2)}`;

function ukDate(iso: string): string {
  const [year, month, day] = iso.split('-');
  return day ? `${day}/${month}/${year}` : iso;
}

/**
 * A Keystone subcontractor's own CIS payment and deduction statements. The database only returns
 * their bills, and only for tax months that have ended.
 */
const MyCisCertificates: React.FC = () => {
  const [account, setAccount] = useState<DirectoryAccount | null>(null);
  const [summary, setSummary] = useState<CISSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    cisService.getMyCISSummary()
      .then((result) => {
        if (!alive) return;
        setAccount(result.account);
        setSummary(result.summary);
      })
      .catch((err) => alive && setError(err instanceof Error ? err.message : 'Failed to load your CIS certificates'))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  const download = async (period: CISMonthlyPeriod) => {
    if (!account) return;
    setDownloading(period.period_start);
    setError(null);
    try {
      await downloadCisCertificate({
        period,
        subcontractorId: account.id,
        verificationNumber: account.cisVerificationNumber || '',
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create the certificate');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 960 }}>
      <Stack spacing={2}>
        <Stack spacing={0.5}>
          <Typography level="h3">CIS Certificates</Typography>
          <Typography level="body-sm" textColor="neutral.600">
            Your monthly statements of payments and CIS deducted by Keystone. Each tax month runs from the 6th to the 5th and appears here once it has ended.
          </Typography>
        </Stack>

        {error && <Alert color="danger">{error}</Alert>}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : !account ? (
          !error && (
            <Alert color="neutral" variant="soft">
              Your sign-in is not linked to a subcontractor account yet. Ask the office to add your Keystone email to your account.
            </Alert>
          )
        ) : (
          <>
            <Card variant="outlined">
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 4 }}>
                <Box>
                  <Typography level="body-xs" textColor="neutral.500">Subcontractor</Typography>
                  <Typography level="title-sm">{account.companyName}</Typography>
                </Box>
                <Box>
                  <Typography level="body-xs" textColor="neutral.500">UTR</Typography>
                  <Typography level="body-sm">{account.utrNumber || '—'}</Typography>
                </Box>
                {account.cisVerificationNumber && (
                  <Box>
                    <Typography level="body-xs" textColor="neutral.500">Verification number</Typography>
                    <Typography level="body-sm">{account.cisVerificationNumber}</Typography>
                  </Box>
                )}
              </Stack>
            </Card>

            <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
              <Table sx={{ minWidth: 620 }}>
                <thead>
                  <tr>
                    <th>Tax month</th>
                    <th style={{ textAlign: 'right' }}>Gross</th>
                    <th style={{ textAlign: 'right' }}>CIS deducted</th>
                    <th style={{ textAlign: 'right' }}>Paid to you</th>
                    <th style={{ width: 170 }} />
                  </tr>
                </thead>
                <tbody>
                  {(summary?.monthly_periods || []).map((period) => (
                    <tr key={period.period_start}>
                      <td>
                        {period.period_label}
                        <Typography level="body-xs" textColor="neutral.500" display="block">
                          {ukDate(period.period_start)} – {ukDate(period.period_end)} · {period.invoices.length} invoice{period.invoices.length === 1 ? '' : 's'}
                        </Typography>
                      </td>
                      <td style={{ textAlign: 'right' }}>{money(period.total_gross)}</td>
                      <td style={{ textAlign: 'right' }}>{money(period.total_cis_deduction)}</td>
                      <td style={{ textAlign: 'right' }}>{money(period.total_net)}</td>
                      <td style={{ textAlign: 'right' }}>
                        <Button size="sm" variant="outlined" loading={downloading === period.period_start} onClick={() => download(period)}>
                          Download PDF
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {(summary?.monthly_periods.length || 0) === 0 && (
                    <tr>
                      <td colSpan={5}>
                        <Typography level="body-sm" textColor="neutral.500" sx={{ py: 2, textAlign: 'center' }}>
                          No CIS certificates yet. They appear here after the 5th of the month following your work.
                        </Typography>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Sheet>
          </>
        )}
      </Stack>
    </Box>
  );
};

export default MyCisCertificates;
